// src/hooks/useTags.js - Hook for accessing polymorphic tags
import useTagStore, { TAG_COLORS, TaggableType } from '../stores/TagStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing tags from TagStore
 *
 * Uses Zustand TagStore for state management.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Tag data and operations
 */
export const useTags = () => {
  const socket = useSocket();

  // All business tags
  const tags = useTagStore(state => state.tags);
  const tagsLoading = useTagStore(state => state.tagsLoading);
  const tagsLoaded = useTagStore(state => state.tagsLoaded);

  // Operations - Tag CRUD
  const loadTags = useTagStore(state => state.loadTags);
  const reloadTags = useTagStore(state => state.reloadTags);
  const createTag = useTagStore(state => state.createTag);
  const updateTag = useTagStore(state => state.updateTag);
  const deleteTag = useTagStore(state => state.deleteTag);

  // Operations - Add/remove tags from entities
  const addTagToEntity = useTagStore(state => state.addTagToEntity);
  const removeTagFromEntity = useTagStore(state => state.removeTagFromEntity);

  // Convenience methods - Customer
  const addTagToCustomer = useTagStore(state => state.addTagToCustomer);
  const removeTagFromCustomer = useTagStore(state => state.removeTagFromCustomer);

  // Convenience methods - Note
  const addTagToNote = useTagStore(state => state.addTagToNote);
  const removeTagFromNote = useTagStore(state => state.removeTagFromNote);

  // Convenience methods - Document
  const addTagToDocument = useTagStore(state => state.addTagToDocument);
  const removeTagFromDocument = useTagStore(state => state.removeTagFromDocument);

  // Utility functions
  const getTagById = useTagStore(state => state.getTagById);
  const getTagByName = useTagStore(state => state.getTagByName);
  const searchTags = useTagStore(state => state.searchTags);

  // Connection state
  const isConnected = useTagStore(state => state.isConnected);

  // Internal initialization state
  const _initialized = useTagStore(state => state._initialized);

  // Fallback initialization check
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('useTags: Store not initialized at app level, trying fallback');
    socket.initializeStore('TagStore');
  }

  return {
    // Data
    tags,
    tagsLoading,
    tagsLoaded,

    // Tag CRUD
    loadTags,
    reloadTags,
    createTag,
    updateTag,
    deleteTag,

    // Add/remove from entities
    addTagToEntity,
    removeTagFromEntity,

    // Customer convenience
    addTagToCustomer,
    removeTagFromCustomer,

    // Note convenience
    addTagToNote,
    removeTagFromNote,

    // Document convenience
    addTagToDocument,
    removeTagFromDocument,

    // Utility functions
    getTagById,
    getTagByName,
    searchTags,

    // Constants
    TAG_COLORS,
    TaggableType,

    // Connection state
    isConnected: socket.isAuthenticated
  };
};

export { TAG_COLORS, TaggableType };
export default useTags;
